import { useEffect, useState } from 'react';
import type { RefObject } from 'react';
import { getFullWidth } from '../../../utils/dom';

const detectHorizontalScroll = (parent: HTMLElement, widthChildren: number) => {
    const widthParent = parent.offsetWidth;

    return widthChildren > widthParent;
};

export const useHorizontalScroll = (
    parentRef: RefObject<HTMLElement>, childrenRefs: Array<RefObject<HTMLElement>>, scrollCallback?: () => void,
) => {
    const [scrollable, setScrollable] = useState(false);
    const [scrollOffset, setScrollOffset] = useState({ left: 0, right: 0 });

    useEffect(() => {
        const widthChildren = childrenRefs
            .map(({ current }) => current)
            .reduce((acc, item) => acc + (item ? getFullWidth(item, false) : 0), 0);

        const calculateScrollOffset = () => {
            if (!parentRef.current) return;
            setScrollOffset({
                left: parentRef.current.scrollLeft,
                right: widthChildren - parentRef.current.offsetWidth - parentRef.current.scrollLeft,
            });
        };
        const handleResize = () => {
            if (!parentRef.current) return;
            setScrollable(detectHorizontalScroll(parentRef.current, widthChildren));
            calculateScrollOffset();
        };
        const handleScroll = () => {
            if (typeof scrollCallback === 'function') scrollCallback();

            calculateScrollOffset();
        };

        window.addEventListener('resize', handleResize);
        parentRef?.current?.addEventListener?.('scroll', handleScroll);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            parentRef?.current?.removeEventListener?.('scroll', handleScroll);
        };
    }, []);

    const onLeftIconClick = () => {
        if (!parentRef.current || !parentRef.current.parentNode) return;
        const scrollWidth = parentRef.current.offsetWidth * 0.75;
        const scrollPosition = Math.max(parentRef.current.scrollLeft - scrollWidth, 0);
        const iconsEl = (parentRef.current.parentNode as ParentNode).querySelector('.Icon-arrow-left') as HTMLElement | null;
        const iconWidth = getFullWidth(iconsEl, true);

        parentRef.current.scrollTo({ left: scrollPosition < iconWidth ? 0 : scrollPosition, behavior: 'smooth' });
    };

    const onRightIconClick = () => {
        if (!parentRef.current) return;
        const widthChildren = childrenRefs
            .map(({ current }) => current)
            .reduce((acc, item) => acc + (item ? getFullWidth(item, false) : 0), 0);
        const scrollWidth = parentRef.current.offsetWidth * 0.75;
        const scrollPosition = Math.min(
            parentRef.current.scrollLeft + scrollWidth,
            widthChildren - parentRef.current.offsetWidth,
        );

        parentRef.current.scrollTo({ left: scrollPosition, behavior: 'smooth' });
    };

    return {
        scrollable,
        scrollOffset,
        onLeftIconClick,
        onRightIconClick,
    };
};
